import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { useSubscribe } from "@/contexts/RealtimeContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Link2, ShieldCheck, ShieldAlert, Wifi, WifiOff, RefreshCw, PlayCircle, ExternalLink,
  MessageSquare, Send, History, Sparkles, Layers, Save as SaveIcon,
} from "lucide-react";

const FIELD_LABELS = {
  GHL_LOCATION_ID: "Location ID (Sub-account)",
  GHL_PIT: "Private Integration Token",
  GHL_WEBHOOK_PUBKEY: "Webhook Ed25519 Public Key (PEM, optional)",
};

const FIELD_HELP = {
  GHL_LOCATION_ID: "Sub-Account Settings → Business Info",
  GHL_PIT: "Sub-Account Settings → Private Integrations → New (scopes: contacts, conversations, opportunities, workflows)",
  GHL_WEBHOOK_PUBKEY: "Optional — paste the Ed25519 public key from GHL to enable inbound signature verification",
};

/**
 * GoHighLevel CRM integration card — shows connection state, lets admin save
 * settings, pick a delinquency pipeline + payment workflow, run bulk sync,
 * and view a live history of sync runs.
 */
export function GhlConfigCard() {
  const [status, setStatus] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [runs, setRuns] = useState([]);
  const [form, setForm] = useState({
    GHL_LOCATION_ID: "",
    GHL_PIT: "",
    GHL_WEBHOOK_PUBKEY: "",
    GHL_DELINQUENCY_PIPELINE_ID: "",
    GHL_PAYMENT_WORKFLOW_ID: "",
  });
  const [saving, setSaving] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkForm, setBulkForm] = useState({ property_id: "all", roles: "resident,vendor" });
  const { properties } = useProperty();

  const loadStatus = useCallback(async () => {
    try { const { data } = await api.get("/ghl/status"); setStatus(data); }
    catch { setStatus(null); }
  }, []);

  const loadConfig = useCallback(async () => {
    try { const { data } = await api.get("/ghl/config"); setCfg(data); }
    catch { setCfg(null); }
  }, []);

  const loadRuns = useCallback(async () => {
    try { const { data } = await api.get("/ghl/sync-runs"); setRuns(data.items || []); }
    catch { setRuns([]); }
  }, []);

  useEffect(() => { loadStatus(); loadRuns(); }, [loadStatus, loadRuns]);
  useEffect(() => { if (status?.configured) loadConfig(); }, [status?.configured, loadConfig]);

  // Live: refresh the run history when a background bulk sync finishes.
  useSubscribe("system", ({ event }) => {
    if (event === "ghl.webhook") loadRuns();
  });

  const save = async () => {
    const upd = Object.fromEntries(Object.entries(form).filter(([, v]) => v && v.trim()));
    if (Object.keys(upd).length === 0) return toast.error("Fill at least one field to save");
    setSaving(true);
    try {
      await api.post("/ghl/settings", upd);
      toast.success("GoHighLevel settings saved");
      setForm({ GHL_LOCATION_ID: "", GHL_PIT: "", GHL_WEBHOOK_PUBKEY: "", GHL_DELINQUENCY_PIPELINE_ID: "", GHL_PAYMENT_WORKFLOW_ID: "" });
      await loadStatus();
      await loadConfig();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setSaving(false);
  };

  const runBulk = async () => {
    setBulkBusy(true);
    try {
      const roles = bulkForm.roles.split(",").map((r) => r.trim()).filter(Boolean);
      const payload = { roles, limit: 500 };
      if (bulkForm.property_id && bulkForm.property_id !== "all") {
        payload.property_id = bulkForm.property_id;
      }
      const { data } = await api.post("/ghl/sync-bulk", payload);
      toast.success(`${data.message}`);
      setBulkOpen(false);
      // Give the background task a moment to start recording, then refresh
      setTimeout(loadRuns, 800);
    } catch (e) {
      if (e?.response?.status === 501) {
        toast.error("Save your GoHighLevel Location ID + PIT first.");
      } else {
        toast.error(apiErrorMessage(e));
      }
    }
    setBulkBusy(false);
  };

  if (!status) {
    return (
      <Card className="border-white/10 bg-card p-5" data-testid="ghl-config">
        <div className="text-sm text-zinc-400">Loading GoHighLevel status…</div>
      </Card>
    );
  }

  const pipelines = cfg?.pipelines || [];
  const workflows = cfg?.workflows || [];

  return (
    <Card className="border-white/10 bg-card p-5" data-testid="ghl-config">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="overline flex items-center gap-1">
            <Link2 className="h-3 w-3" /> GoHighLevel CRM
          </div>
          <div className="text-lg font-semibold font-display mt-1">
            Sync residents & vendors into your GHL sub-account
          </div>
        </div>
        <div className="flex items-center gap-2">
          {status.configured ? (
            <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10" data-testid="ghl-status-badge">
              <ShieldCheck className="h-3 w-3 mr-1" /> Configured
            </Badge>
          ) : (
            <Badge variant="outline" className="border-amber-500/40 text-amber-300 bg-amber-500/10" data-testid="ghl-status-badge">
              <ShieldAlert className="h-3 w-3 mr-1" /> Not configured
            </Badge>
          )}
          {status.configured && (
            status.ping === "ok" ? (
              <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10" data-testid="ghl-ping-badge">
                <Wifi className="h-3 w-3 mr-1" /> Live
              </Badge>
            ) : (
              <Badge variant="outline" className="border-rose-500/40 text-rose-300 bg-rose-500/10" data-testid="ghl-ping-badge">
                <WifiOff className="h-3 w-3 mr-1" /> Ping failed
              </Badge>
            )
          )}
          <Button size="sm" variant="outline" className="border-white/10" onClick={() => { loadStatus(); loadConfig(); loadRuns(); }} data-testid="ghl-refresh">
            <RefreshCw className="h-3 w-3 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      <div className="text-sm text-zinc-400 mt-2">
        Uses the LeadConnector v2 API via a Private Integration Token.
        Contacts get tagged with role, aging bucket, and delinquency state so your GHL automations can take it from there.
      </div>
      {status.missing?.length > 0 && (
        <div className="text-[11px] text-amber-300 mt-2" data-testid="ghl-missing">
          Missing: {status.missing.map((k) => FIELD_LABELS[k] || k).join(", ")}
        </div>
      )}
      {status.location_id_masked && (
        <div className="text-[11px] text-zinc-500 mt-2" data-testid="ghl-location-masked">
          Connected to location <span className="font-mono">{status.location_id_masked}</span>
        </div>
      )}

      {/* Settings form */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        {Object.keys(FIELD_LABELS).map((k) => (
          <div key={k} className={k === "GHL_WEBHOOK_PUBKEY" ? "md:col-span-2" : ""}>
            <Label className="text-xs">{FIELD_LABELS[k]}</Label>
            <Input
              data-testid={`ghl-${k}`}
              value={form[k] || ""}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              placeholder={FIELD_HELP[k]}
              type={k === "GHL_PIT" ? "password" : "text"}
            />
          </div>
        ))}
      </div>

      {/* Automation selectors — only useful once pipelines/workflows load */}
      {status.configured && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Delinquency pipeline</Label>
            <Select value={form.GHL_DELINQUENCY_PIPELINE_ID || "__none"} onValueChange={(v) => setForm({ ...form, GHL_DELINQUENCY_PIPELINE_ID: v === "__none" ? "" : v })}>
              <SelectTrigger data-testid="ghl-pipeline" className="bg-white/[0.02] border-white/10">
                <SelectValue placeholder={pipelines.length === 0 ? "No pipelines loaded" : "Choose a pipeline"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">— None —</SelectItem>
                {pipelines.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-zinc-500 mt-1">
              Delinquent residents will land in this pipeline (default stage).
            </div>
          </div>
          <div>
            <Label className="text-xs">Payment workflow</Label>
            <Select value={form.GHL_PAYMENT_WORKFLOW_ID || "__none"} onValueChange={(v) => setForm({ ...form, GHL_PAYMENT_WORKFLOW_ID: v === "__none" ? "" : v })}>
              <SelectTrigger data-testid="ghl-workflow" className="bg-white/[0.02] border-white/10">
                <SelectValue placeholder={workflows.length === 0 ? "No workflows loaded" : "Choose a workflow"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">— None —</SelectItem>
                {workflows.map((w) => (
                  <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-zinc-500 mt-1">
              Fired against each contact when their payment posts.
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <Button data-testid="ghl-save" onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
          {saving ? "Saving…" : "Save GoHighLevel settings"}
        </Button>
        {status.configured && (
          <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
            <DialogTrigger asChild>
              <Button data-testid="ghl-bulk-open" variant="outline" className="border-white/10">
                <PlayCircle className="h-3.5 w-3.5 mr-1" /> Bulk sync to GHL
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-white/10">
              <DialogHeader>
                <DialogTitle><PlayCircle className="h-4 w-4 inline mr-1" /> Bulk sync to GoHighLevel</DialogTitle>
                <DialogDescription className="text-xs text-zinc-400">
                  Upserts every resident/vendor as a GHL contact with tags for role, aging, and delinquency.
                  Runs in the background — you&apos;ll get a realtime notification when it finishes.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Property</Label>
                  <Select value={bulkForm.property_id} onValueChange={(v) => setBulkForm({ ...bulkForm, property_id: v })}>
                    <SelectTrigger data-testid="ghl-bulk-property" className="bg-white/[0.02] border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All properties</SelectItem>
                      {properties.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Roles (comma-separated)</Label>
                  <Input data-testid="ghl-bulk-roles" value={bulkForm.roles} onChange={(e) => setBulkForm({ ...bulkForm, roles: e.target.value })} placeholder="resident, vendor" />
                  <div className="text-[10px] text-zinc-500 mt-1">Default: resident + vendor. Admin accounts are never synced.</div>
                </div>
                <Button data-testid="ghl-bulk-run" onClick={runBulk} disabled={bulkBusy} className="w-full bg-orange-500 hover:bg-orange-600">
                  <Sparkles className="h-3.5 w-3.5 mr-1" /> {bulkBusy ? "Queuing…" : "Start bulk sync"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
        <a
          href="https://app.gohighlevel.com"
          target="_blank"
          rel="noreferrer"
          className="text-[11px] text-zinc-500 hover:text-white inline-flex items-center gap-1"
        >
          <ExternalLink className="h-3 w-3" /> Open GHL
        </a>
      </div>

      {/* Sync run history */}
      {runs.length > 0 && (
        <div className="mt-5">
          <div className="overline flex items-center gap-1 mb-2">
            <History className="h-3 w-3" /> Recent sync runs
          </div>
          <div className="rounded-md border border-white/10 divide-y divide-white/5" data-testid="ghl-runs">
            {runs.slice(0, 6).map((r) => (
              <div key={r.id} className="px-3 py-2 text-xs flex items-center justify-between" data-testid={`ghl-run-${r.id}`}>
                <div>
                  <div className="text-zinc-200">
                    {r.ok}/{r.requested} synced
                    {r.failed > 0 && <span className="text-rose-300"> · {r.failed} failed</span>}
                  </div>
                  <div className="text-[10px] text-zinc-500">{new Date(r.at).toLocaleString()} · by {r.actor}</div>
                </div>
                <Badge variant="outline" className={r.failed === 0 ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-amber-500/40 text-amber-300 bg-amber-500/10"}>
                  {r.failed === 0 ? "clean" : "partial"}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * Inline "Sync to GHL" button for resident/vendor rows.
 *   uid: the user id (resident or vendor)
 *   contactId: existing ghl_contact_id if any (renders as a small linked badge)
 *   onSynced: called with { contact_id } after a successful sync
 *   compact: true for row usage (icon-only), false for detail page (full label)
 */
export function GhlSyncButton({ uid, contactId, onSynced, compact = false, disabled }) {
  const [busy, setBusy] = useState(false);
  const [cid, setCid] = useState(contactId || null);
  useEffect(() => { setCid(contactId || null); }, [contactId]);

  const sync = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setBusy(true);
    try {
      const { data } = await api.post(`/ghl/sync-contact/${uid}`);
      const newId = data?.contact_id || null;
      setCid(newId);
      onSynced?.({ contact_id: newId });
      toast.success(newId ? `Synced (${newId.slice(0, 8)}…)` : "Synced to GHL");
    } catch (err) {
      if (err?.response?.status === 501) {
        toast.error("GoHighLevel isn't configured — open Admin → Settings.");
      } else {
        toast.error(apiErrorMessage(err));
      }
    }
    setBusy(false);
  };

  if (compact) {
    return (
      <button
        type="button"
        onClick={sync}
        disabled={busy || disabled}
        data-testid={`ghl-sync-${uid}`}
        className={
          "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] transition " +
          (cid
            ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20"
            : "border-white/10 text-zinc-300 hover:bg-white/[0.05]")
        }
        title={cid ? `GHL contact ${cid}` : "Sync this contact to GoHighLevel"}
      >
        <Link2 className="h-3 w-3" /> {busy ? "…" : cid ? "GHL" : "Sync"}
      </button>
    );
  }

  return (
    <Button
      size="sm"
      variant="outline"
      className="border-white/10"
      onClick={sync}
      disabled={busy || disabled}
      data-testid={`ghl-sync-${uid}`}
    >
      <Link2 className="h-3.5 w-3.5 mr-1" />
      {busy ? "Syncing…" : cid ? `Re-sync (${cid.slice(0, 8)}…)` : "Sync to GHL"}
    </Button>
  );
}

/**
 * GoHighLevel field mapping card — pairs each internal Prestigious field
 * (balance, unit, aging_bucket, etc.) with a GHL custom-field ID so the
 * sync writes real customFields[] instead of relying only on tags.
 */
export function GhlFieldMapCard() {
  const [status, setStatus] = useState(null);
  const [cfg, setCfg] = useState(null);      // pipelines/workflows/customFields
  const [mapState, setMapState] = useState(null); // {choices:[{key,label}], mapping:{key:gh_id}}
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, m] = await Promise.all([api.get("/ghl/status"), api.get("/ghl/field-map")]);
      setStatus(s.data);
      setMapState(m.data);
      if (s.data?.configured) {
        try { const { data } = await api.get("/ghl/config"); setCfg(data); }
        catch { setCfg({ customFields: [] }); }
      }
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/ghl/settings", { GHL_FIELD_MAP: mapState.mapping || {} });
      toast.success("Field mapping saved");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setSaving(false);
  };

  const setPair = (internalKey, ghFieldId) => {
    setMapState((prev) => ({
      ...prev,
      mapping: { ...(prev.mapping || {}), [internalKey]: ghFieldId === "__none" ? "" : ghFieldId },
    }));
  };
  const clearPair = (internalKey) => {
    setMapState((prev) => {
      const m = { ...(prev.mapping || {}) };
      delete m[internalKey];
      return { ...prev, mapping: m };
    });
  };

  if (busy) {
    return (
      <Card className="border-white/10 bg-card p-5" data-testid="ghl-fieldmap">
        <div className="text-sm text-zinc-400">Loading field mapping…</div>
      </Card>
    );
  }
  const configured = status?.configured;
  const customFields = cfg?.customFields || [];
  const mapping = mapState?.mapping || {};
  const filled = Object.values(mapping).filter(Boolean).length;

  return (
    <Card className="border-white/10 bg-card p-5" data-testid="ghl-fieldmap">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="overline flex items-center gap-1">
            <Layers className="h-3 w-3" /> Field mapping
          </div>
          <div className="text-lg font-semibold font-display mt-1">
            Push Prestigious fields into your GHL custom fields
          </div>
        </div>
        <Badge variant="outline" className="border-white/10">
          {filled} of {mapState?.choices?.length || 0} mapped
        </Badge>
      </div>
      <div className="text-sm text-zinc-400 mt-2">
        Tags cover role, aging bucket, and delinquency out of the box.
        Use this to also push balance, unit, days-past-due, and property name
        into their own custom fields so your GHL reports, filters, and dynamic
        merge tags can use them directly.
      </div>

      {!configured && (
        <div className="text-xs text-amber-300 mt-3">
          Save your Location ID + Private Integration Token first — mappings need a live GHL connection.
        </div>
      )}

      {configured && customFields.length === 0 && (
        <div className="text-xs text-amber-300 mt-3">
          Couldn&apos;t load custom fields from GHL. Make sure your PIT includes the
          <code className="mx-1 text-zinc-300">locations/customFields.readonly</code> scope,
          then refresh.
        </div>
      )}

      {configured && customFields.length > 0 && (
        <div className="mt-4 space-y-2">
          {(mapState?.choices || []).map((choice) => {
            const paired = mapping[choice.key] || "";
            const pairedField = customFields.find((f) => f.id === paired);
            return (
              <div
                key={choice.key}
                className="grid grid-cols-1 md:grid-cols-[220px_1fr_36px] items-center gap-2 p-2 rounded-md border border-white/5 hover:border-white/10"
                data-testid={`ghl-fieldmap-row-${choice.key}`}
              >
                <div>
                  <div className="text-sm text-zinc-200">{choice.label}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">internal: {choice.key}</div>
                </div>
                <Select value={paired || "__none"} onValueChange={(v) => setPair(choice.key, v)}>
                  <SelectTrigger
                    data-testid={`ghl-fieldmap-select-${choice.key}`}
                    className="bg-white/[0.02] border-white/10"
                  >
                    <SelectValue placeholder="— Not mapped —" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64 overflow-y-auto">
                    <SelectItem value="__none">— Not mapped —</SelectItem>
                    {customFields.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.name || f.fieldKey || f.id}
                        {f.dataType ? ` · ${f.dataType}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  onClick={() => clearPair(choice.key)}
                  disabled={!paired}
                  className="text-[10px] text-zinc-500 hover:text-rose-300 disabled:opacity-40"
                  title="Clear"
                  data-testid={`ghl-fieldmap-clear-${choice.key}`}
                >
                  {pairedField ? "×" : ""}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <Button data-testid="ghl-fieldmap-save" onClick={save} disabled={saving || !configured} className="bg-orange-500 hover:bg-orange-600">
          <SaveIcon className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving…" : "Save mapping"}
        </Button>
        <Button variant="outline" className="border-white/10" size="sm" onClick={load} disabled={busy}>
          <RefreshCw className="h-3 w-3 mr-1" /> Reload from GHL
        </Button>
        <div className="text-[10px] text-zinc-500 ml-2">
          Next sync will populate the mapped fields on every contact.
        </div>
      </div>
    </Card>
  );
}

export function GhlActionsPanel({ uid, contactId }) {
  const [status, setStatus] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [wfId, setWfId] = useState("");
  const [wfBusy, setWfBusy] = useState(false);

  const [msgOpen, setMsgOpen] = useState(false);
  const [msgBusy, setMsgBusy] = useState(false);
  const [msg, setMsg] = useState({ type: "SMS", message: "", subject: "", html: "" });

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/ghl/status"); setStatus(data); }
      catch { setStatus(null); }
    })();
  }, []);
  useEffect(() => {
    if (!status?.configured) return;
    (async () => {
      try { const { data } = await api.get("/ghl/config"); setCfg(data); }
      catch { setCfg(null); }
    })();
  }, [status?.configured]);

  const trigger = async () => {
    if (!wfId) return toast.error("Pick a workflow");
    setWfBusy(true);
    try {
      await api.post(`/ghl/trigger-workflow/${uid}`, { workflow_id: wfId });
      toast.success("Workflow triggered in GoHighLevel");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
    setWfBusy(false);
  };

  const send = async () => {
    if (msg.type === "SMS" && !msg.message.trim()) return toast.error("Type a message");
    if (msg.type === "Email" && !msg.subject.trim()) return toast.error("Subject is required for email");
    setMsgBusy(true);
    try {
      const payload = { type: msg.type };
      if (msg.type === "SMS") payload.message = msg.message;
      else { payload.subject = msg.subject; payload.html = msg.html || msg.message; }
      await api.post(`/ghl/send-message/${uid}`, payload);
      toast.success(`${msg.type} sent via GoHighLevel`);
      setMsg({ type: msg.type, message: "", subject: "", html: "" });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const body = detail?.ghl_body || (typeof detail === "string" ? detail : apiErrorMessage(e));
      toast.error(`GHL: ${String(body).slice(0, 160)}`);
    } finally {
      // Auto-close on both success and error — the toast carries the outcome.
      setMsgOpen(false);
      setMsgBusy(false);
    }
  };

  if (!status) return null;

  return (
    <Card className="border-white/10 bg-card p-5" data-testid="ghl-actions">
      <div className="overline flex items-center gap-1 mb-3">
        <Link2 className="h-3 w-3" /> GoHighLevel CRM
      </div>
      {!status.configured ? (
        <div className="text-sm text-amber-300 flex items-center gap-2">
          <ShieldAlert className="h-4 w-4" />
          GoHighLevel isn&apos;t configured — <a href="/admin/settings" className="underline">open Settings</a> to connect.
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-xs text-zinc-400">
            <GhlSyncButton uid={uid} contactId={contactId} />
            {contactId && (
              <span className="text-[11px] text-zinc-500 font-mono">contact {contactId.slice(0, 12)}…</span>
            )}
          </div>

          <div>
            <Label className="text-xs">Trigger a workflow</Label>
            <div className="flex gap-2 mt-1">
              <Select value={wfId || "__none"} onValueChange={(v) => setWfId(v === "__none" ? "" : v)}>
                <SelectTrigger data-testid="ghl-trigger-workflow" className="bg-white/[0.02] border-white/10 flex-1">
                  <SelectValue placeholder={cfg?.workflows?.length ? "Pick a workflow" : "Loading workflows…"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— Select —</SelectItem>
                  {(cfg?.workflows || []).map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" onClick={trigger} disabled={!wfId || wfBusy} className="bg-orange-500 hover:bg-orange-600" data-testid="ghl-trigger-btn">
                <PlayCircle className="h-3.5 w-3.5 mr-1" /> {wfBusy ? "…" : "Trigger"}
              </Button>
            </div>
          </div>

          <div>
            <Label className="text-xs">Reach out via GHL Conversations</Label>
            <div className="mt-1">
              <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="border-white/10 w-full" data-testid="ghl-message-open">
                    <MessageSquare className="h-3.5 w-3.5 mr-1" /> Send SMS / Email
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-white/10">
                  <DialogHeader>
                    <DialogTitle><Send className="h-4 w-4 inline mr-1" /> Send via GoHighLevel</DialogTitle>
                    <DialogDescription className="text-xs text-zinc-400">
                      Uses the resident&apos;s registered channel. GHL threads land in your CRM inbox.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>Channel</Label>
                      <Select value={msg.type} onValueChange={(v) => setMsg({ ...msg, type: v })}>
                        <SelectTrigger data-testid="ghl-msg-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="SMS">SMS</SelectItem>
                          <SelectItem value="Email">Email</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {msg.type === "Email" && (
                      <div>
                        <Label>Subject</Label>
                        <Input data-testid="ghl-msg-subject" value={msg.subject} onChange={(e) => setMsg({ ...msg, subject: e.target.value })} placeholder="Regarding your account" />
                      </div>
                    )}
                    <div>
                      <Label>{msg.type === "SMS" ? "Message" : "Body (HTML allowed)"}</Label>
                      <Textarea
                        data-testid="ghl-msg-body"
                        rows={4}
                        value={msg.type === "SMS" ? msg.message : (msg.html || msg.message)}
                        onChange={(e) => msg.type === "SMS"
                          ? setMsg({ ...msg, message: e.target.value })
                          : setMsg({ ...msg, html: e.target.value, message: e.target.value })
                        }
                        placeholder={msg.type === "SMS" ? "Reminder: your February dues are past due." : "<p>Hi,</p><p>…</p>"}
                      />
                    </div>
                    <Button data-testid="ghl-msg-send" onClick={send} disabled={msgBusy} className="w-full bg-orange-500 hover:bg-orange-600">
                      <Send className="h-3.5 w-3.5 mr-1" /> {msgBusy ? "Sending…" : `Send ${msg.type}`}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
